import {
  createRootRoute,
  Outlet,
  useLocation,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useEffect } from "react";

import { AddHostModal } from "../components/AddHostModal";
import AuthGuard from "../components/AuthGuard";
import { PasteWarningDialog } from "../components/PasteWarningDialog";
import { PerfHud } from "../components/PerfHud";
import { SettingsModal } from "../components/SettingsModal";
import { ToastHost } from "../components/ToastHost";
import type { ServerHost } from "../lib/api";
import { authClient } from "../lib/auth-client";
import { darken, hexToRgba, lighten } from "../lib/colors";
import { useQuery, useZero } from "../lib/zero";
import { useAppStore } from "../store";
import { useSettings } from "../stores/settingsStore";
import { useTerminalWorkspace } from "../stores/terminalWorkspaceStore";
import { queries } from "../zero/queries";

// Routes rendered without AuthGuard (see the branch in RootComponent).
const PUBLIC_PATHS = new Set(["/delete-account", "/oauth-sign-in", "/oauth-consent"]);

// The Zero `host` row carries only safe columns; pad it back into the
// `ServerHost` shape `serverToLocalHost` expects (it only reads a subset).
type ZeroHostRow = {
  id: string;
  userId: string;
  name: string;
  hostname: string;
  port: number;
  sshUser: string;
  authMethod: ServerHost["authMethod"];
  connectionMode: ServerHost["connectionMode"];
  os: string | null;
  note: string | null;
  isDeleted: boolean | null;
};

const zeroHostToServer = (r: ZeroHostRow): ServerHost => ({
  id: r.id,
  name: r.name,
  hostname: r.hostname,
  port: r.port,
  sshUser: r.sshUser,
  authMethod: r.authMethod,
  connectionMode: r.connectionMode ?? "direct",
  os: r.os,
  tags: [],
  note: r.note,
  sharedWith: [],
  isDeleted: r.isDeleted ?? false,
  deletedAt: null,
  lastSeenAt: null,
  createdAt: "",
  createdBy: r.userId,
  updatedAt: "",
  updatedBy: null,
  userId: r.userId,
});

interface RootSearch {
  modal?: "add" | "settings";
  section?: "general" | "terminal" | "shortcuts";
}

export const Route = createRootRoute({
  validateSearch: (raw): RootSearch => {
    const out: RootSearch = {};
    if (raw.modal === "add" || raw.modal === "settings") out.modal = raw.modal;
    if (
      raw.section === "general" || raw.section === "terminal" || raw.section === "shortcuts"
    ) {
      out.section = raw.section;
    }
    return out;
  },
  component: RootLayout,
});

function RootLayout() {
  const navigate = useNavigate();
  const { modal, section } = useSearch({ from: "__root__" }) as RootSearch;

  const addServerHost = useAppStore((s) => s.addServerHost);
  const setHosts = useAppStore((s) => s.setHosts);
  const theme = useAppStore((s) => s.theme);

  // Hosts now sync live from Zero (zero-cache) instead of a REST GET. The
  // reactive query reflects host create/delete (still done via REST) once
  // zero-cache replicates Postgres. On sign-out we clear the local list.
  const { data: session, isPending: sessionPending } = authClient.useSession();
  const userId = session?.user?.id;
  // Synced query — runs server-side at /api/zero/query with the auth ctx. Gate
  // on the Zero instance's userID (set only once the JWT is in place) so we
  // don't fire the query against the still-anonymous instance and get a 401.
  const z = useZero();
  const [hostRows, hostsInfo] = useQuery(z.userID ? queries.host.mine() : false);
  const setHostsLoading = useAppStore((s) => s.setHostsLoading);
  useEffect(() => {
    // Drive the sidebar skeleton: loading until the session resolves and Zero
    // reports the host query 'complete' (or rows have already arrived).
    if (sessionPending) {
      setHostsLoading(true);
      return;
    }
    if (!userId) {
      setHosts([]);
      setHostsLoading(false);
      return;
    }
    if (hostRows) setHosts((hostRows as ZeroHostRow[]).map(zeroHostToServer));
    const ready = hostsInfo?.type === "complete" || (hostRows?.length ?? 0) > 0;
    setHostsLoading(!ready);
  }, [userId, sessionPending, hostRows, hostsInfo, setHosts, setHostsLoading]);
  const setTheme = useAppStore((s) => s.setTheme);
  const accent = useAppStore((s) => s.accent);
  const setAccent = useAppStore((s) => s.setAccent);

  // Apply theme + accent to <html> whenever they change. Lives at the root
  // because it must always be mounted, independent of which route is active.
  useEffect(() => {
    const resolved: "dark" | "light" =
      theme === "system"
        ? (matchMedia?.("(prefers-color-scheme: light)").matches ? "light" : "dark")
        : theme;
    document.documentElement.setAttribute("data-theme", resolved);
  }, [theme]);

  // Perf HUD toggle: ⌘/Ctrl+Shift+P. Lives at the root so it works on every
  // route; the terminal's custom key handler passes this combo through.
  useEffect(() => {
    const onDown = (e: KeyboardEvent) => {
      // Without the repeat guard, holding the combo rapid-toggles the HUD at
      // the OS key-repeat rate (mount/unmount churn + a localStorage write
      // per flip, landing on whatever parity the repeat count happens to hit).
      if (e.repeat) return;
      if (
        (e.metaKey || e.ctrlKey) &&
        e.shiftKey &&
        !e.altKey &&
        e.key.toLowerCase() === "p"
      ) {
        e.preventDefault();
        useSettings.getState().togglePerfHud();
      }
    };
    window.addEventListener("keydown", onDown);
    return () => window.removeEventListener("keydown", onDown);
  }, []);

  useEffect(() => {
    if (!accent) return;
    const root = document.documentElement;
    root.style.setProperty("--accent", accent);
    root.style.setProperty("--accent-soft", hexToRgba(accent, 0.14));
    root.style.setProperty("--accent-glow", hexToRgba(accent, 0.25));
    root.style.setProperty("--accent-2", lighten(accent, 0.18));
    root.style.setProperty("--accent-deep", darken(accent, 0.18));
  }, [accent]);

  const closeModal = () =>
    navigate({ to: ".", search: (prev) => ({ ...prev, modal: undefined, section: undefined }) });

  // Publicly reachable routes render WITHOUT AuthGuard: the account-deletion
  // page must load signed-out (Google OAuth verification links it directly),
  // and the OAuth provider pages (MCP connector sign-in/consent) own their
  // signed-out handling. Hooks above run unconditionally either way (they are
  // signed-out safe — the Zero host query is gated on z.userID), so only the
  // JSX branches.
  const { pathname } = useLocation();
  if (PUBLIC_PATHS.has(pathname)) {
    return (
      <>
        <Outlet />
        <ToastHost />
      </>
    );
  }

  return (
    <AuthGuard>
      <Outlet />

      <ToastHost />
      <PasteWarningDialog />
      <PerfHud />

      {modal === "add" && (
        <AddHostModal
          onClose={closeModal}
          onAdd={(host) => {
            addServerHost(host);
            useTerminalWorkspace.getState().requestFocusHost(host.id);
            navigate({ to: "/hosts" });
          }}
        />
      )}

      {modal === "settings" && (
        <SettingsModal
          onClose={closeModal}
          theme={theme}
          setTheme={setTheme}
          accent={accent}
          setAccent={setAccent}
          section={section}
          onSectionChange={(s) =>
            navigate({ to: ".", search: (prev) => ({ ...prev, section: s }) })
          }
        />
      )}
    </AuthGuard>
  );
}
